import React from "react";
import { motion } from "framer-motion";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  Building2, 
  Globe2, 
  MessageSquare 
} from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

const ContactEs = () => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log("Form submitted");
  };

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.FEMALE_CALL_CENTER_1_272}
            alt="Agente de Call Center Femenino Servicio al Cliente"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-background" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            Contacto
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="text-xl text-white/80 max-w-2xl mx-auto"
          >
            Estamos aquí para ofrecer soluciones profesionales para sus proyectos. 
            Solicite una cotización o consulte a nuestro equipo técnico.
          </motion.p>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Contact Form / Quote Request */}
            <motion.div
              id="formulario-cotizacion"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <motion.div variants={staggerItem} className="mb-10">
                <h2 className="text-3xl font-bold text-primary mb-4">Formulario de Cotización e Información</h2>
                <p className="text-muted-foreground">
                  Complete el siguiente formulario para que nuestros representantes de ventas se pongan en contacto con usted.
                </p>
              </motion.div>

              <motion.form 
                variants={staggerItem}
                onSubmit={handleSubmit} 
                className="space-y-6 bg-card p-8 rounded-2xl border border-border shadow-sm"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="first-name">Su Nombre</Label>
                    <Input id="first-name" placeholder="Ej: Juan" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last-name">Su Apellido</Label>
                    <Input id="last-name" placeholder="Ej: García" required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Su Dirección de Correo Electrónico</Label>
                    <Input id="email" type="email" placeholder="juan@empresa.com" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Su Número de Teléfono</Label>
                    <Input id="phone" type="tel" placeholder="+34 6XX XXX XXX" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interest">Área de Interés</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="architectural">Sistemas Arquitectónicos</SelectItem>
                      <SelectItem value="industrial">Perfiles Industriales</SelectItem>
                      <SelectItem value="surface">Tratamiento Superficial / Pintura</SelectItem>
                      <SelectItem value="mechanical">Procesamiento Mecánico</SelectItem>
                      <SelectItem value="other">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Su Mensaje</Label>
                  <Textarea 
                    id="message" 
                    placeholder="Puede escribir los detalles de su proyecto o sus preguntas aquí..." 
                    className="min-h-[150px]"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full md:w-auto px-12 group">
                  Enviar Formulario
                  <Send className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </motion.form>
            </motion.div>

            {/* Contact Details */}
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-5 space-y-8"
            >
              {/* Office Details */}
              <motion.div variants={staggerItem}>
                <Card className="border-none shadow-none bg-accent/30 overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="p-3 bg-primary rounded-xl text-white">
                        <Building2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-primary">Oficina Central y Fábrica</h3>
                        <p className="text-muted-foreground mt-1">Complejo de Producción Industrial</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="flex gap-4">
                        <MapPin className="w-5 h-5 text-primary shrink-0" />
                        <span className="text-foreground">
                          Pelitli Mahallesi Tavşanlı Yolu Caddesi No: 123 <br />
                          Gebze / KOCAELİ
                        </span>
                      </div>
                      <div className="flex gap-4">
                        <Phone className="w-5 h-5 text-primary shrink-0" />
                        <span className="text-foreground font-medium">+90 (212) 555 00 00</span>
                      </div>
                      <div className="flex gap-4">
                        <Mail className="w-5 h-5 text-primary shrink-0" />
                        <span className="text-foreground">info@mimalum.com.tr</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Working Hours */}
              <motion.div variants={staggerItem}>
                <Card className="border border-border">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <Clock className="w-6 h-6 text-primary" />
                      <h3 className="text-xl font-bold text-primary">Horarios de Trabajo</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="flex justify-between border-b border-border pb-2">
                        <span className="text-muted-foreground">Lunes - Viernes</span>
                        <span className="font-semibold">08:30 - 18:30</span>
                      </div>
                      <div className="flex justify-between border-b border-border pb-2">
                        <span className="text-muted-foreground">Sábado</span>
                        <span className="font-semibold">09:00 - 14:00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Domingo</span>
                        <span className="text-destructive font-medium">Cerrado</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Quick Links */}
              <motion.div variants={staggerItem} className="grid grid-cols-2 gap-4">
                <Button variant="outline" className="h-auto py-6 flex flex-col gap-2">
                  <Globe2 className="w-5 h-5 text-primary" />
                  <span className="text-xs uppercase tracking-wider font-bold">Línea de Exportación</span>
                </Button>
                <Button variant="outline" className="h-auto py-6 flex flex-col gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <span className="text-xs uppercase tracking-wider font-bold">Soporte WhatsApp</span>
                </Button>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="w-full h-[500px] bg-muted relative">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3014.2234567890123!2d29.4027777!3d40.8027777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac82d2b5c5c5d%3A0x5c5c5c5c5c5c5c5c!2sPelitli%20Mahallesi%2C%20Tav%C5%9Fanl%C4%B1%20Yolu%20Cd.%20No%3A123%2C%2041400%20Gebze%2FKocaeli%2C%20Turkey!5e0!3m2!1str!2str!4v1707310000000!5m2!1str!2str"
          className="w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-500"
          allowFullScreen
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="Ubicación de la Fábrica"
        ></iframe>
        
        <div className="absolute bottom-8 left-8 z-10 hidden md:block">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-primary text-white p-6 rounded-lg shadow-2xl max-w-xs"
          >
            <h4 className="font-bold mb-2">Obtener Direcciones</h4>
            <p className="text-sm text-white/80 mb-4">Use el mapa para ver las rutas de acceso más rápidas a nuestra fábrica.</p>
            <Button variant="secondary" size="sm" className="w-full" asChild>
              <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">Google Maps</a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white overflow-hidden relative">
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-bold mb-2">Su Socio de Soluciones Sectoriales</h2>
              <p className="text-white/70 text-lg">Haga clic para descargar nuestro catálogo o acceder a documentos técnicos.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="secondary" size="lg" className="px-8">
                Descargar Catálogos
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary px-8">
                Nuestros Certificados
              </Button>
            </div>
          </div>
        </div>
        {/* Background Decorative Element */}
        <div className="absolute -right-24 -bottom-24 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
      </section>
    </div>
  );
};

export default ContactEs;